import { getQueryParameters, regionApi, regionRequest } from './regionApi';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const regionHaInstanceApi = regionApi.injectEndpoints({
  endpoints: (build) => ({
    /* -------------------------------------------------- */
    /* Single region instance list                        */
    /* -------------------------------------------------- */
    getInstanceList: build.query<any, Record<string, any>>({
      query: ({ region, account }) =>
        regionRequest(region, 'instance/get_instance_list', {
          method: 'POST',
          body: getQueryParameters(account)
        })
    }),

    /* -------------------------------------------------- */
    /* Multi-region instance list                         */
    /* -------------------------------------------------- */
    getRegionsInstanceList: build.query<any, Record<string, any>>({
      async queryFn(arg, _api, _extraOptions, fetchWithBQ) {
        const regions = arg?.regions || [];
        const accountRegions = Array.isArray(arg?.account_region)
          ? arg.account_region
          : arg?.account_region?.alias
            ? [arg.account_region]
            : [];

        const validAliases = accountRegions.map((r: any) => r.alias);

        const resArr = await Promise.all(
          regions.map(async (region: any) => {
            const isValid = validAliases.includes(region.alias);

            if (!isValid) {
              return { data: [], region };
            }

            const res = await fetchWithBQ(
              regionRequest(region, 'instance/get_instance_list', {
                method: 'POST',
                body: getQueryParameters({
                  ...arg,
                  instid: arg?.instid
                })
              })
            );

            return res.data
              ? { data: res.data, region }
              : {
                  error: res.error as FetchBaseQueryError,
                  region
                };
          })
        );

        return { data: resArr };
      },

      providesTags: ['RegionInstance']
    }),

    /* -------------------------------------------------- */
    /* Instance info                                     */
    /* -------------------------------------------------- */
    getInstanceInfo: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_instance_info', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        })
    }),

    /* -------------------------------------------------- */
    /* Extra info                                        */
    /* -------------------------------------------------- */
    getHaInstanceExtraInfo: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_active_instance_extra_info', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        })
    }),

    /* -------------------------------------------------- */
    /* Instance state                                    */
    /* -------------------------------------------------- */
    getHaInstanceState: build.query<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/get_instance_state', {
          method: 'POST',
          body: {
            group_id: body.group_id,
            account_id: body.account_id || '',
            user_id: body.user_id,
            access_from_user_id: body.access_from_user_id,
            instance_id: body.instance_id || '',
            action: body.action || '',
            license_sn: body.license_sn || ''
          }
        })
    }),

    /* -------------------------------------------------- */
    /* VM live status                                    */
    /* -------------------------------------------------- */
    getVmLiveStatus: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_vm_live_status', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        }),
      providesTags: (_r, _e, arg) => [
        {
          type: 'VmStatus',
          id: arg?.instance?.instance_id
        }
      ]
    }),

    /* -------------------------------------------------- */
    /* Historic quota                                    */
    /* -------------------------------------------------- */
    getInstanceHistoricQuota: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_instance_historic_quota', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        })
    }),

    /* -------------------------------------------------- */
    /* Access URL                                        */
    /* -------------------------------------------------- */
    getHaInstanceAccessUrl: build.query<any, Record<string, any>>({
      query: ({ region, account, instance, ha_id }) =>
        regionRequest(region, 'instance/get_instance_access_url', {
          method: 'POST',
          body: getQueryParameters(
            {
              ...account,
              ...instance
            },
            { ha_id: ha_id || '' }
          )
        }),
      keepUnusedDataFor: 5
    }),

    /* -------------------------------------------------- */
    /* Demo Instance Access URL                                        */
    /* -------------------------------------------------- */
    getDemoInstanceAccessUrl: build.query<any, Record<string, any>>({
      query: ({ region, instance }) =>
        regionRequest(region, 'instance/get_demo_instance_access_url', {
          method: 'POST',
          body: {
            ...instance
          }
        }),
      keepUnusedDataFor: 5
    }),

    /* -------------------------------------------------- */
    /* Provision                                         */
    /* -------------------------------------------------- */
    provision: build.mutation<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/provision_instance', {
          method: 'POST',
          body: {
            account_id: body.account_id || '',
            tz_code: body.tz_code || '',
            image_version: body.image_version || '',
            adom_version: body.adom_version || '',
            license_sn: body.license_sn || '',
            try_trial: body.try_trial || '',
            is_multi_tenant: body.is_multi_tenant || false
          }
        })
    }),

    /* -------------------------------------------------- */
    /* Status                                            */
    /* -------------------------------------------------- */
    getStatus: build.query<any, Record<string, any>>({
      query: ({ region, license_sn, task_id, account_id }) =>
        regionRequest(region, 'instance/get_provision_status', {
          method: 'POST',
          body: {
            license_sn: license_sn || '',
            task_id: task_id || '',
            account_id: account_id || ''
          }
        })
    }),

    /* -------------------------------------------------- */
    /* Resize / Upgrade / Reboot                         */
    /* -------------------------------------------------- */
    resize: build.mutation<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/resize', {
          method: 'POST',
          body: {
            account_id: body.account_id || '',
            instance_id: body.instance_id || '',
            user_id: body.user_id,
            access_from_user_id: body.access_from_user_id,
            eta_mins: body.eta_mins || 0
          }
        })
    }),

    upgrade: build.mutation<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/upgrade', {
          method: 'POST',
          body: {
            account_id: body.account_id || '',
            user_id: body.user_id,
            access_from_user_id: body.access_from_user_id,
            instance_id: body.instance_id || '',
            to_version: body.to_version,
            eta_mins: body.eta_mins || 0
          }
        })
    }),

    /* -------------------------------------------------- */
    /* Reboot                                            */
    /* -------------------------------------------------- */
    reboot: build.mutation<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/reboot_instance', {
          method: 'POST',
          body: {
            group_id: body.group_id || '',
            account_id: body.account_id || '',
            user_id: body.user_id,
            access_from_user_id: body.access_from_user_id,
            instance_id: body.instance_id || ''
          }
        }),
      invalidatesTags: ['RegionInstance']
    }),

    /* -------------------------------------------------- */
    /* Upgrade request                                   */
    /* -------------------------------------------------- */
    requestUpgrade: build.mutation<any, Record<string, any>>({
      query: ({ region, ...body }) =>
        regionRequest(region, 'instance/request_upgrade', {
          method: 'POST',
          body: {
            account_id: body.account_id || '',
            user_id: body.user_id,
            access_from_user_id: body.access_from_user_id || '',
            instance_id: body.instance_id || '',
            name: body.name || '',
            email: body.email || '',
            deployment: body.deployment || '',
            group_id: body.group_id || '',
            route_id: body.route_id || ''
          }
        }),
      invalidatesTags: ['UpgradeRequest']
    }),

    /* -------------------------------------------------- */
    /* Firmware download                                 */
    /* -------------------------------------------------- */
    firmwareDownload: build.mutation<any, Record<string, any>>({
      query: ({ region, data }) =>
        regionRequest(region, 'firmware_download', {
          method: 'GET',
          params: {
            version: data?.firmwareVersion,
            platform: data?.platform
          }
        }),

      transformResponse: (response: any, _meta, arg) => {
        const version = arg?.data?.firmwareVersion;

        const url = window.URL || window.webkitURL;

        const blob = url.createObjectURL(response?.data);

        const a = document.createElement('a');
        a.href = blob;
        a.target = '_blank';
        a.download = version;
        a.click();

        return { data: null };
      }
    }),

    /* -------------------------------------------------- */
    /* Upgrade requests                                  */
    /* -------------------------------------------------- */
    getUpgradeRequest: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_upgrade_request', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        }),
      providesTags: ['UpgradeRequest']
    }),

    /* -------------------------------------------------- */
    getUpgradeRoutes: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_upgrade_routes', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        })
    }),

    /* -------------------------------------------------- */
    getHistoricQuota: build.query<any, Record<string, any>>({
      query: ({ region, account, instance }) =>
        regionRequest(region, 'instance/get_instance_historic_quota', {
          method: 'POST',
          body: getQueryParameters({
            ...account,
            ...instance
          })
        })
    })
  })
});

export const {
  useGetInstanceListQuery,
  useLazyGetInstanceListQuery,
  useGetRegionsInstanceListQuery,
  useGetVmLiveStatusQuery,
  useLazyGetVmLiveStatusQuery,
  useGetInstanceInfoQuery,
  useLazyGetInstanceInfoQuery,
  useGetHaInstanceExtraInfoQuery,
  useLazyGetHaInstanceAccessUrlQuery,
  useLazyGetDemoInstanceAccessUrlQuery,
  useProvisionMutation,
  useRebootMutation,
  useUpgradeMutation,
  useResizeMutation,
  useRequestUpgradeMutation,
  useGetStatusQuery,
  useGetHaInstanceStateQuery,
  useGetUpgradeRequestQuery,
  useLazyGetUpgradeRequestQuery,
  useGetUpgradeRoutesQuery,
  useGetHistoricQuotaQuery
} = regionHaInstanceApi;
